import { ArrowLeft, FileText, Smartphone } from 'lucide-react';
import Screen from '../../demo/Screen';
import FitToWidth from '../../ui/FitToWidth';
import BodyFigure from '../../mocks/BodyFigure';
import { BrandMark } from '../../mocks/ReportCover';
import { BarsGlyph, ChatPhoneGlyph, CheckDiscGlyph, GroupGlyph, RiseGlyph, TrackGlyph } from '../../mocks/ProductGlyphs';
import TrendReport from './TrendReport';
import { DATES, MARKERS, YEAR_AGO_INDEX, changeOverYear, changeSinceLast } from './TrendChart';
import './Trends.css';

const SCREEN = 'product.trends';

const POINTS = [
  {
    key: 'track',
    icon: TrackGlyph,
    tone: 'blue',
    title: 'Track over time',
    text: 'See multiple past results, not just a single number.',
  },
  {
    key: 'spot',
    icon: RiseGlyph,
    tone: 'green',
    title: 'Spot changes early',
    text: 'Automatic indicators for improving, stable or out-of-range values.',
  },
  {
    key: 'back',
    icon: GroupGlyph,
    tone: 'purple',
    title: 'Bring patients back',
    text: 'Helps patients see progress and the value of regular testing.',
  },
];

/* Lucide outlines where the photo shows outlines; drawn glyphs where it shows fills. */
const DocIcon = () => <FileText size={24} strokeWidth={2} aria-hidden="true" />;
const BarsIcon = () => <BarsGlyph size={24} />;
const PhoneIcon = () => <Smartphone size={24} strokeWidth={2} aria-hidden="true" />;
const ChatIcon = () => <ChatPhoneGlyph size={26} />;

const PRODUCTS = [
  { key: 'reports', icon: DocIcon, tone: 'blue', title: 'Smart Reports', text: 'Trends and insights in every report.' },
  {
    key: 'analytics',
    icon: BarsIcon,
    tone: 'purple',
    title: 'Analytics',
    text: 'View longitudinal data across your patient base.',
  },
  { key: 'app', icon: PhoneIcon, tone: 'green', title: 'Lab app', text: 'Patients can track their progress.' },
  { key: 'whatsapp', icon: ChatIcon, tone: 'mint', title: 'WhatsApp', text: 'Automated trends and timely reminders.' },
];

/* The phone and WhatsApp mocks show the same patient's Total Cholesterol as the chart. */
const TOTAL = MARKERS.find((m) => m.key === 'total');
const TOTAL_LATEST = TOTAL.values[TOTAL.values.length - 1];
const TOTAL_YEAR = changeOverYear(TOTAL);
const TOTAL_SINCE_LAST = changeSinceLast(TOTAL);

/* The phone's mini chart: the last year of Total Cholesterol, from the data. */
function PhoneChart() {
  const values = TOTAL.values.slice(YEAR_AGO_INDEX);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const x = (i) => 8 + (84 * i) / (values.length - 1);
  const y = (v) => 34 - ((v - lo) / Math.max(1, hi - lo)) * 24;
  const pts = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg className="tr-phone__chart" viewBox="0 0 100 46" aria-hidden="true" focusable="false">
      <rect x="2" y="4" width="96" height="10" fill="#fde4e6" />
      <rect x="2" y="14" width="96" height="10" fill="#fdf1d2" />
      <rect x="2" y="24" width="96" height="14" fill="#e3f5ea" />
      <polyline points={pts} fill="none" stroke="#e5484d" strokeWidth="1.6" />
      {values.map((v, i) => (
        <circle key={DATES[YEAR_AGO_INDEX + i]} cx={x(i)} cy={y(v)} r="1.8" fill="#e5484d" />
      ))}
      <path d="M2 43h96" stroke="#e3eaf6" strokeWidth="1" />
    </svg>
  );
}

/* The report cover and a results page behind it (decorative). */
function MiniReport() {
  return (
    <div className="tr-cover-stack" aria-hidden="true">
      <div className="tr-results-page">
        <span className="tr-results-page__brand">
          <BrandMark className="tr-brand__mark" />
          niroggyan
        </span>
        <span className="tr-results-page__band" />
        {['#1fa463', '#1fa463', '#f2b01f', '#1fa463', '#e5484d', '#1fa463', '#1fa463', '#f2b01f', '#1fa463'].map(
          (c, i) => (
            <span key={`row-${i + 1}`} className="tr-results-page__row" style={{ '--dot': c }} />
          ),
        )}
      </div>
      <div className="tr-cover">
        <span className="tr-brand tr-cover__brand">
          <BrandMark className="tr-brand__mark" />
          niroggyan
        </span>
        <BodyFigure className="tr-cover__figure" />
        <strong className="tr-cover__title">
          Smart
          <br />
          Health Report
        </strong>
        <span className="tr-cover__lines">
          <span />
          <span />
        </span>
      </div>
    </div>
  );
}

function Mocks() {
  return (
    <div className="tr-mocks">
      <MiniReport />

      <div className="tr-phone">
        <div className="tr-phone__screen">
          <p className="tr-phone__title">Your progress</p>
          <div className="tr-phone__card">
            <span>Total Cholesterol</span>
            <strong>{`${TOTAL_LATEST} ${TOTAL.unit}`}</strong>
            <PhoneChart />
            <span className="tr-phone__trend">Improving</span>
            <span className="tr-phone__change">{`${TOTAL_YEAR.down ? '-' : '+'}${TOTAL_YEAR.percent}% in 12 months`}</span>
          </div>
          <span className="tr-phone__btn" aria-hidden="true">
            View full report
          </span>
        </div>
      </div>

      <figure className="tr-wa">
        <figcaption className="tr-wa__head">
          <ArrowLeft size={14} strokeWidth={2.4} aria-hidden="true" />
          <span className="tr-wa__avatar" aria-hidden="true">
            N
          </span>
          NirogGyan Lab
          <CheckDiscGlyph size={12} className="tr-wa__verified" color="#25d366" />
          <span className="sr-only">: WhatsApp message</span>
        </figcaption>
        <div className="tr-wa__bubble">
          <p>
            Your latest Total Cholesterol is <strong>{`${TOTAL_LATEST} ${TOTAL.unit}`}</strong>.
          </p>
          <p>
            This is <strong>{`${TOTAL_SINCE_LAST.percent}%`}</strong>{' '}
            {TOTAL_SINCE_LAST.down ? 'lower' : 'higher'} than your last test. Great progress!
          </p>
          <p>Keep up the good work.</p>
          <span className="tr-wa__cta" aria-hidden="true">
            Book your next checkup →
          </span>
          <span className="tr-wa__time">10:24 AM</span>
        </div>
      </figure>
    </div>
  );
}

export default function Trends() {
  return (
    <Screen id={SCREEN} title="Trends" className="tr">
      {/* The photo's layout (1476 px of content), scaled to fit beside the product sidebar */}
      <FitToWidth designWidth={1476} minWidth={960}>
        <div className="tr-hero">
          <div className="tr-intro">
            <p className="eyebrow tr-eyebrow">Trends &amp; longitudinal analysis</p>
            <h1 className="headline tr-headline">
              Every result <br />
              <em>in context.</em>
            </h1>
            <ul className="tr-points">
              {POINTS.map(({ key, icon: Icon, tone, title, text }) => (
                <li key={key}>
                  <span className={`tr-points__icon tr-points__icon--${tone}`} aria-hidden="true">
                    <Icon size={34} />
                  </span>
                  <span>
                    <strong>{title}</strong>
                    <span>{text}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <TrendReport />
        </div>

        <section className="tr-across" aria-labelledby="tr-across-title">
          <div className="tr-across__text">
            <h2 id="tr-across-title" className="tr-across__title">
              Trends work across <br />
              your NirogGyan products.
            </h2>
            <ul className="tr-across__list">
              {PRODUCTS.map(({ key, icon: Icon, tone, title, text }) => (
                <li key={key}>
                  <span className={`tr-across__icon tr-across__icon--${tone}`} aria-hidden="true">
                    <Icon />
                  </span>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
          <Mocks />
        </section>
      </FitToWidth>
    </Screen>
  );
}
